/* Helpers the dashboard widgets share. Money and its definitions come from the
   Statistics page's own helpers, so a figure here always equals the same figure there. */

import { useTranslation } from 'react-i18next';
import type { StatPair, StatSeller, StatisticsResponse } from '../../api/client';
import { CREDITED, HATCH, SELLER_COLOR, pick, type MoneyMode } from '../../components/Statistics/unified/model';
import { useDash } from '../context';
import type { Period } from '../model';

/** "+12%" / "−6%" vs the previous period; empty when there is nothing to compare with. */
export function change(cur: number, prev: number | null | undefined): string {
  if (prev == null || !Number.isFinite(prev) || prev === 0) return '';
  const p = Math.round(((cur - prev) / Math.abs(prev)) * 100);
  return p > 0 ? `+${p}%` : p < 0 ? `−${Math.abs(p)}%` : '0%';
}

/** The money "Sold in chat": PPVs and tips with a Seller (AI, Team or Unknown). */
export function soldInChat(sellers: Record<StatSeller, StatPair> | undefined, mode: MoneyMode): number {
  return CREDITED.reduce((a, k) => a + pick(sellers?.[k], mode), 0);
}

/** Made by AI: AI revenue as a share of Sold in chat, in percent; null when nothing was sold in chat. */
export function aiShare(sellers: Record<StatSeller, StatPair> | undefined, mode: MoneyMode): number | null {
  const sold = soldInChat(sellers, mode);
  return sold ? (pick(sellers?.ai, mode) / sold) * 100 : null;
}

export const ppvSoldCount = (stats: StatisticsResponse | null) =>
  stats ? CREDITED.reduce((a, k) => a + (stats.money.ppv_sold[k]?.count ?? 0), 0) : 0;

/** A Seller's fill; "Not sold in chat" is hatched, as on Statistics. */
export const sellerFill = (k: StatSeller) => (k === 'not_in_chat' ? HATCH : SELLER_COLOR[k]);

/** Purple shades for rows that have no colour of their own (Accounts). */
export const SHADES = ['var(--om-accent)', 'var(--om-accent-2)', 'var(--om-accent-3)', 'var(--om-accent-4)', 'var(--om-accent-deep)', 'var(--om-accent-hover)'];

/** "Today" / "7 days" / "30 days", plus "· 4 of 9 accounts" when money covers fewer Accounts than are in view. */
export function useMetaText() {
  const { t } = useTranslation('dashboardWidgets');
  const { statsScope } = useDash();
  const scope = statsScope && statsScope.n < statsScope.of ? t('widget.scope', { n: statsScope.n, of: statsScope.of }) : '';
  return {
    period: (per: Period) => t(`period.${per}`),
    /** Joins the parts that are set with " · ", adding the Stats-access note for money widgets. */
    join: (parts: (string | false | null | undefined)[], money = true) => [...parts, money ? scope : ''].filter(Boolean).join(' · '),
  };
}
